import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        // 2026-10-28 : reapplique une previsualisation couleur "manuelle" en attente
        // (posee par le clic sur une pastille, voir le script inline dans
        // snippets/variant-main-picker.liquid et la methode previewMedia ci-dessous),
        // pour qu'elle ne soit pas silencieusement ecrasee par la photo de la variante
        // resolue par le serveur (qui ne reflete qu'une seule des deux options
        // couleur sur un produit a options independantes "pièce 1"/"pièce 2").
        const section = newMediaGallery.closest('.shopify-section, dialog');
        const pendingMediaId = section?.dataset.pendingPreviewMedia;

        if (pendingMediaId && typeof newMediaGallery.previewMedia === 'function') {
          // Le slideshow nouvellement attache finit sa propre initialisation async
          // (connectedCallback) ; on laisse passer un frame pour qu'elle soit prete.
          requestAnimationFrame(() => newMediaGallery.previewMedia(pendingMediaId));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * 2026-10-28 : previsualise instantanement un media donne (par id) dans le
   * slideshow de la galerie, sans attendre une resolution complete de variante.
   * Permet au clic sur une pastille de couleur d'afficher la photo correspondante
   * meme quand cette option ne pilote pas, seule, le featured_media de la variante
   * resolue (cas d'un produit a deux options couleur independantes "pièce 1"/"pièce 2").
   * Voir le script inline dans snippets/variant-main-picker.liquid qui appelle ceci.
   *
   * NOTE : contrairement au slideshow des cartes produit (product-card.js), les
   * <slideshow-slide> de la galerie fiche produit ne portent PAS d'attribut
   * slide-id -- l'id du media n'est present que sur un element enfant
   * (data-media-id, pose par la section media-gallery elle-meme). On ne peut donc
   * pas utiliser slideshow.select({id}) ici (qui cherche slide-id) ; on recherche
   * l'index de la slide correspondante et on selectionne par index.
   * @param {string} id - L'id du media a afficher.
   */
  previewMedia(id) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const slides = slideshow.refs?.slides;
    if (!slides) return;

    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${id}"]`));
    if (index === -1) return;

    slideshow.select(index, undefined, { animate: false });
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
